import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Form, TvInput, TvAlert, Spinner, TvButton, TvDiv, TvLabel, TvSmall } from "@trustvid/design-system";
import { ForgotPassword } from "../../../services/auth";
import AuthSplitLayout from "./AuthSplitLayout";
import { AUTH_FIELD_LABEL_CLASS, FORGOT_PASSWORD_PAGE_COPY } from "./loginPageContent";

const validationSchema = Yup.object({
  email: Yup.string().email("Invalid email address").required("Email is required"),
});

const ForgetPasswordPage = () => {
  document.title = FORGOT_PASSWORD_PAGE_COPY.pageTitle;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError("");
      setSuccessMessage("");
      try {
        const response = await ForgotPassword(values);

        if (response?.error) {
          setError(response.error);
        } else {
          setSuccessMessage(FORGOT_PASSWORD_PAGE_COPY.successMessage);
          formik.resetForm();
        }
      } catch (err) {
        setError("An unexpected error occurred. Please try again later.");
      } finally {
        setLoading(false);
      }
    },
  });

  const hasEmail = formik.values.email.trim().length > 0;

  return (
    <AuthSplitLayout
      pageTestId="forgot-password-page"
      cardTestId="forgot-password-form-card"
      brandTestId="auth-platform-brand"
      showCardHeading={false}
      cardTitle={FORGOT_PASSWORD_PAGE_COPY.pageTitle}
      welcomeHeading={FORGOT_PASSWORD_PAGE_COPY.pageTitle}
      welcomeHeadingTestId="forgot-password-card-title"
      cardSubtitle={FORGOT_PASSWORD_PAGE_COPY.cardSubtitle}
      featurePanelHeading={FORGOT_PASSWORD_PAGE_COPY.featurePanelHeading}
    >
      <Form onSubmit={formik.handleSubmit} data-testid="forgot-password-form" className="tv-login-page__form">
        {error ? (
          <TvAlert color="danger" className="mb-3" data-testid="forgot-password-error" fade={false}>
            {error}
          </TvAlert>
        ) : null}
        {successMessage ? (
          <TvAlert color="success" className="mb-3" data-testid="forgot-password-success" fade={false}>
            {successMessage}
          </TvAlert>
        ) : null}

        <TvDiv className="mb-4">
          <TvLabel htmlFor="forgot-password-email" className={AUTH_FIELD_LABEL_CLASS}>
            {FORGOT_PASSWORD_PAGE_COPY.emailLabel}
          </TvLabel>
          <TvInput
            id="forgot-password-email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder={FORGOT_PASSWORD_PAGE_COPY.recoveryEmailPlaceholder}
            aria-label={FORGOT_PASSWORD_PAGE_COPY.emailLabel}
            className="tv-login-page__field"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.email}
            invalid={formik.touched.email && !!formik.errors.email}
          />
          {formik.touched.email && formik.errors.email ? (
            <TvSmall className="text-danger d-block mt-1">{formik.errors.email}</TvSmall>
          ) : null}
        </TvDiv>

        <TvDiv className="d-grid mb-3">
          <TvButton
            color="primary"
            type="submit"
            disabled={loading}
            className={`tv-login-page__submit${!hasEmail && !loading ? " disabled" : ""}`}
          >
            {loading ? <Spinner size="sm" color="light" /> : FORGOT_PASSWORD_PAGE_COPY.resetButton}
          </TvButton>
        </TvDiv>

        <TvDiv className="text-center text-lg-start">
          <Link to="/login" className="tv-login-page__text-link fw-semibold text-decoration-none">
            {FORGOT_PASSWORD_PAGE_COPY.backToSignIn}
          </Link>
        </TvDiv>
      </Form>
    </AuthSplitLayout>
  );
};

export default ForgetPasswordPage;
